import React from 'react';
import { string, func, bool } from 'prop-types';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import { displayPrice } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { propTypes } from '../../util/types';
import { formatMoney } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';

import { AspectRatioWrapper, AvatarSmall, NamedLink, ResponsiveImage } from '..';

import css from './ListingCard.module.css';
import IconFavorite from '../IconFavorite/IconFavorite';
import IconStar from '../IconStar/IconStar';
import IconFilledFavorite from '../IconFilledFavorite/IconFilledFavorite';
import LocationBadge from '../LocationBadge/LocationBadge';
import { FaShippingFast } from 'react-icons/fa';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

export const PriceMaybe = props => {
  const { price, publicData, config, intl } = props;
  const { listingType } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);
  if (!showPrice && price) {
    return null;
  }

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);
  return (
    <div className={css.price}>
      <div className={css.priceValue} title={priceTitle}>
        {formattedPrice}
      </div>
      {isBookable ? (
        <div className={css.perUnit}>
          <FormattedMessage id="ListingCard.perUnit" values={{ unitType: publicData?.unitType }} />
        </div>
      ) : null}
    </div>
  );
};

export const ListingCardComponent = props => {
  const config = useConfiguration();
  const {
    className,
    rootClassName,
    intl,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo,
    onToggleFavorites,
    currentUser,
  } = props;
  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', description = '', price, publicData, location } = currentListing.attributes;
  const shippingEnabled = publicData?.shippingData?.isShippingActive === true || publicData?.shippingEnabled === true;
  const translations = publicData.translations?.[intl.locale] || {};
  const intlTitle = translations.title || title;
  const intlDescription = translations.description || description;
  const { locationContext } = publicData;

  const slug = createSlug(title);
  const author = ensureUser(listing.author);
  const authorName = author.attributes.profile.displayName;
  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  const { avg: averageRating } = listing?.attributes?.metadata?.rating || {};

  const ratings = averageRating ? (
    <div className={css.ratings}>
      <IconStar className={css.iconStar} />
      <b>{isNaN(averageRating) ? averageRating : averageRating.toFixed(2)}</b>
    </div>
  ) : (
    <div className={css.ratings}>
      <IconStar className={css.iconStar} />
      <b>
        {' '}
        <FormattedMessage id="OrderPanel.noReviews" />
      </b>
    </div>
  );

  const isFavorite = currentUser?.attributes?.profile?.privateData?.favorites?.includes(id);

  const onToggleFavoritesEvent = event => {
    event.preventDefault();
    onToggleFavorites(isFavorite, id);
  };

  return (
    <NamedLink className={classes} name="ListingPage" params={{ id, slug }}>
      <AspectRatioWrapper
        className={css.aspectRatioWrapper}
        width={aspectWidth}
        height={aspectHeight}
        {...setActivePropsMaybe}
      >
        <LazyImage
          rootClassName={css.rootForImage}
          alt={intlTitle}
          image={firstImage}
          variants={variants}
          sizes={renderSizes}
        />
        <div className={css.badgeGroup}>
          <LocationBadge locationContext={locationContext} isSmall />

          {shippingEnabled && (
            <div className={css.deliveryBadge}>
              <FaShippingFast className={css.deliveryIcon} />
            </div>
          )}
        </div>
      </AspectRatioWrapper>
      <div className={css.info}>
        <div className={css.mainInfo}>
          <div className={css.title}>
            {richText(intlTitle, {
              longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
              longWordClass: css.longWord,
            })}
            {typeof currentUser !== 'undefined' && typeof onToggleFavorites === 'function' ? (
              <div className={css.favoriteIcon} onClick={onToggleFavoritesEvent}>
                {isFavorite ? <IconFilledFavorite /> : <IconFavorite />}
              </div>
            ) : null}
          </div>
          <div className={css.description}>{intlDescription}</div>
        </div>
        <div className={css.bottomInfo}>
          {showAuthorInfo ? (
            <AvatarSmall user={author} className={css.authorAvatar} disableProfileLink />
          ) : null}
          <div className={css.authorInfo}>
            {showAuthorInfo ? (
              <>
                <FormattedMessage id="ListingCard.author" values={{ authorName }} />
                {ratings}
              </>
            ) : null}
          </div>

          <PriceMaybe price={price} publicData={publicData} config={config} intl={intl} />
        </div>
      </div>
    </NamedLink>
  );
};

ListingCardComponent.defaultProps = {
  className: null,
  rootClassName: null,
  renderSizes: null,
  setActiveListing: null,
  showAuthorInfo: true,
};

ListingCardComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  listing: propTypes.listing.isRequired,
  showAuthorInfo: bool,

  // Responsive image sizes hint
  renderSizes: string,

  setActiveListing: func,
};

export default injectIntl(ListingCardComponent);
